import { Modal } from "antd";
import { ArrowUpRight, BookOpen, ListChecks, Search, ShieldCheck, X } from "lucide-react";

import { SETUP_MEETING_BOOKING_URL } from "constants/constants";

const OLLIE_WELCOME_IMAGE = `${process.env.PUBLIC_URL}/assets/images/icons/ollie-claim-welcome.png`;

const SETUP_MEETING_TOPICS = [
	{
		icon: ListChecks,
		title: "Set up your review rules",
		description:
			"Walk through your submittal requirements and review criteria for each project type, including applicable code citations."
	},
	{
		icon: BookOpen,
		title: "Confirm your adopted codes",
		description: "Review code editions and local amendments together so Ollie starts with the right foundation."
	},
	{
		icon: Search,
		title: "Get ready for your first review",
		description:
			"See how Ollie applies your requirements to pre-screen plans to help your reviewers be more efficient and give applicants clear, accurate guidance."
	}
];

type SetupMeetingModalProps = {
	isOpen: boolean;
	onClose: () => void;
	// Called when the user clicks the button that opens the booking page in a new tab.
	onScheduleClick: () => void;
};

/**
 * Modal that asks the user to book a 30-minute meeting to finish the setup of the jurisdiction.
 * SetupMeetingPrompt decides when it opens.
 */
const SetupMeetingModal = ({ isOpen, onClose, onScheduleClick }: SetupMeetingModalProps) => {

	const topicRows = SETUP_MEETING_TOPICS.map(topic => {
		const TopicIcon = topic.icon;

		return (
			<div key={topic.title} className="setup-meeting-modal-topic">
				<div className="setup-meeting-modal-topic-icon">
					<TopicIcon size={16} />
				</div>
				<div className="setup-meeting-modal-topic-text">
					<div className="setup-meeting-modal-topic-title">{topic.title}</div>
					<div className="setup-meeting-modal-topic-description">{topic.description}</div>
				</div>
			</div>
		);
	});

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			footer={null}
			title={null}
			closeIcon={<X size={16} />}
			centered
			width={576}
			className="setup-meeting-modal"
			rootClassName="setup-meeting-modal-root"
		>
			<div className="setup-meeting-modal-header">
				<div className="setup-meeting-modal-heading">
					<img src={OLLIE_WELCOME_IMAGE} alt="Ollie" className="setup-meeting-modal-ollie-image" />
					<h2 className="setup-meeting-modal-title">Finish setting up your Ollie.</h2>
				</div>
				<p className="setup-meeting-modal-description">
					Meet with our Customer Success Team for 30 minutes to configure Ollie’s review rules, adopted codes, and
					submittal requirements.
				</p>
			</div>

			<div className="setup-meeting-modal-body">
				<div className="setup-meeting-modal-topics">{topicRows}</div>

				<a
					href={SETUP_MEETING_BOOKING_URL}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onScheduleClick}
					className="setup-meeting-modal-book-button"
				>
					Schedule my 30-minute setup
					<ArrowUpRight size={16} />
				</a>

				<div className="setup-meeting-modal-note">
					<ShieldCheck size={16} className="setup-meeting-modal-note-icon" />
					Free for AHJs. Setup support included.
				</div>
			</div>
		</Modal>
	);
};

export default SetupMeetingModal;
